import { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthDepartments } from '@/api/auth';
import {
  getMonthSummary,
  getDailySummary,
  getGoodsSummary,
  getReportDetails,
  getCategorySummary,
  getPurchasePlanSummary,
  getMonthPurchasePlanSummary,
  exportReport,
  type ReportFilterParams,
  type ReportSummaryRow,
  type ReportDetailOrder,
  type ReportCategorySummaryRow,
  type ReportPurchasePlanRow,
  type ReportMonthPurchasePlanRow,
  type ReportGoodsSummaryRow,
  type ReportExportType,
} from '@/api/report';
import { getUser } from '@/utils/auth';
import { alertError } from '@/utils/message';
import SearchableSelect from '@/components/base/SearchableSelect';
import DateRangePicker from '@/components/base/DateRangePicker';
import SpecFilterSelect, { type SpecFilterOption } from '@/components/base/SpecFilterSelect';
import UsageRatioBar from '@/components/base/UsageRatioBar';

const tabs = [
  { id: 'monthly' as const, label: '月度汇总表', icon: 'ri-calendar-check-line' },
  { id: 'daily' as const, label: '日汇总表', icon: 'ri-calendar-todo-line' },
  { id: 'goods' as const, label: '物资汇总表', icon: 'ri-box-3-line' },
  { id: 'detail' as const, label: '明细表', icon: 'ri-file-list-3-line' },
  { id: 'category' as const, label: '分类统计表', icon: 'ri-pie-chart-line' },
  { id: 'plan' as const, label: '年度预算表', icon: 'ri-file-chart-line' },
  { id: 'monthPlan' as const, label: '月度计划表', icon: 'ri-calendar-schedule-line' },
];

const TAB_TO_EXPORT_TYPE: Record<(typeof tabs)[number]['id'], ReportExportType> = {
  monthly: 'month-summary',
  daily: 'daily-summary',
  goods: 'goods-summary',
  detail: 'details',
  category: 'category-summary',
  plan: 'year-purchase-summary',
  monthPlan: 'month-purchase-summary',
};

interface SummaryViewRow {
  period: string;
  deptName: string;
  goodsQuantity: number;
  amount: number;
  cost: number;
  totalAmount: number;
}

function mapSummaryRows(rows: ReportSummaryRow[]): SummaryViewRow[] {
  return rows
    .map((row) => ({
      period: String(row.d || '').trim(),
      deptName: String(row.department_name || '未知科室'),
      goodsQuantity: Number(row.goods_quantity ?? 0) || 0,
      amount: Number(row.amount ?? 0) || 0,
      cost: Number(row.cost ?? 0) || 0,
      totalAmount: Number(row.total ?? 0) || 0,
    }))
    .filter((row) => !!row.period)
    .sort((a, b) => a.period.localeCompare(b.period) || a.deptName.localeCompare(b.deptName));
}

interface GoodsSummaryViewRow {
  categoryName: string;
  goodsName: string;
  skuName: string;
  code: string;
  quantity: number;
  unit: string;
  price: number;
  amount: number;
}

function mapGoodsSummaryRows(rows: ReportGoodsSummaryRow[]): GoodsSummaryViewRow[] {
  return rows
    .map((row) => ({
      categoryName: String(row.category_name || '未分类').trim() || '未分类',
      goodsName: String(row.goods_name || '-').trim() || '-',
      skuName: String(row.sku_name || '-').trim() || '-',
      code: String(row.code || '').trim() || '-',
      quantity: Number(row.quantity ?? 0) || 0,
      unit: String(row.unit || '-').trim() || '-',
      price: Number(row.price ?? 0) || 0,
      amount: Number(row.amount ?? 0) || 0,
    }))
    .sort(
      (a, b) =>
        a.categoryName.localeCompare(b.categoryName) ||
        a.goodsName.localeCompare(b.goodsName) ||
        a.skuName.localeCompare(b.skuName),
    );
}

interface CategoryViewRow {
  period: string;
  deptName: string;
  parentCategoryName: string;
  categoryName: string;
  amount: number;
  cost: number;
  totalAmount: number;
}

function mapCategoryRows(rows: ReportCategorySummaryRow[]): CategoryViewRow[] {
  return rows
    .map((row) => ({
      period: String(row.d || '').trim(),
      deptName: String(row.department_name || '未知科室'),
      parentCategoryName: String(row.parent_category_name || '未分类').trim() || '未分类',
      categoryName: row.category_name ? String(row.category_name).trim() : '-',
      amount: Number(row.amount ?? 0) || 0,
      cost: Number(row.cost ?? 0) || 0,
      totalAmount: Number(row.total ?? 0) || 0,
    }))
    .filter((row) => !!row.period)
    .sort(
      (a, b) =>
        a.period.localeCompare(b.period) ||
        a.deptName.localeCompare(b.deptName) ||
        a.parentCategoryName.localeCompare(b.parentCategoryName) ||
        a.categoryName.localeCompare(b.categoryName),
    );
}

interface PlanViewRow {
  period: string;
  deptName: string;
  monthUsed: number;
  /** 年度总预算，用于计算当月占比 */
  quota: number;
  /** 当月使用 / 年度总预算（百分比） */
  usageRatio: number;
}

/** footer 年度汇总（来自科室级 quota / remain_amount / used_amount / usage_ratio） */
interface PlanFooterTotals {
  quota: number;
  remainAmount: number;
  usedAmount: number;
  usageRatio: number;
}

function mapPurchasePlanRows(rows: ReportPurchasePlanRow[]): PlanViewRow[] {
  const result: PlanViewRow[] = [];
  rows.forEach((dept) => {
    const quota = Number(dept.quota ?? 0) || 0;
    const deptName = String(dept.department_name || '未知科室');
    (dept.months || []).forEach((m) => {
      const period = String(m.d || '').trim();
      if (!period) return;
      const monthUsed = Number(m.used_amount ?? 0) || 0;
      result.push({
        period,
        deptName,
        monthUsed,
        quota,
        usageRatio: quota > 0 ? (monthUsed / quota) * 100 : 0,
      });
    });
  });
  return result.sort(
    (a, b) => a.period.localeCompare(b.period) || a.deptName.localeCompare(b.deptName),
  );
}

interface MonthPlanViewRow {
  period: string;
  deptName: string;
  goodsName: string;
  specName: string;
  code: string;
  unit: string;
  planQuantity: number;
  usedQuantity: number;
  returnQuantity: number;
  remainQuantity: number;
  price: number;
  planAmount: number;
  usedAmount: number;
  returnAmount: number;
  remainAmount: number;
  usageRatio: number;
}

function mapMonthPurchasePlanRows(rows: ReportMonthPurchasePlanRow[]): MonthPlanViewRow[] {
  return rows
    .map((row) => ({
      period: String(row.d || '').trim(),
      deptName: String(row.department_name || '未知科室'),
      goodsName: String(row.goods_name || '-'),
      specName: String(row.spec_name || '-'),
      code: String(row.code || '-'),
      unit: String(row.unit || '-'),
      planQuantity: Number(row.plan_quantity ?? 0) || 0,
      usedQuantity: Number(row.used_quantity ?? 0) || 0,
      returnQuantity: Number(row.return_quantity ?? 0) || 0,
      remainQuantity: Number(row.remain_quantity ?? 0) || 0,
      price: Number(row.price ?? 0) || 0,
      planAmount: Number(row.plan_amount ?? 0) || 0,
      usedAmount: Number(row.used_amount ?? 0) || 0,
      returnAmount: Number(row.return_amount ?? 0) || 0,
      remainAmount: Number(row.remain_amount ?? 0) || 0,
      usageRatio: Number(row.usage_ratio ?? 0) || 0,
    }))
    .filter((row) => !!row.period);
}

function mapPurchasePlanFooter(rows: ReportPurchasePlanRow[]): PlanFooterTotals {
  const quota = rows.reduce((s, r) => s + (Number(r.quota ?? 0) || 0), 0);
  const usedAmount = rows.reduce((s, r) => s + (Number(r.used_amount ?? 0) || 0), 0);
  const remainAmount = rows.reduce((s, r) => s + (Number(r.remain_amount ?? 0) || 0), 0);
  const usageRatio =
    rows.length === 1
      ? Number(rows[0].usage_ratio ?? 0) || (quota > 0 ? (usedAmount / quota) * 100 : 0)
      : quota > 0
        ? (usedAmount / quota) * 100
        : 0;
  return { quota, remainAmount, usedAmount, usageRatio };
}

/** 分类表合并：相同科室 / 一级分类（多二级时）；月份随科室展示，不跨科室合并 */
function buildCategoryMergeSpans(rows: CategoryViewRow[]) {
  const n = rows.length;
  const periodSpan = new Array<number>(n).fill(0);
  const deptSpan = new Array<number>(n).fill(0);
  const parentSpan = new Array<number>(n).fill(0);

  for (let i = 0; i < n; ) {
    let j = i + 1;
    while (
      j < n &&
      rows[j].period === rows[i].period &&
      rows[j].deptName === rows[i].deptName
    ) {
      j += 1;
    }
    // 月份与科室同范围：每个科室块都显示月份
    periodSpan[i] = j - i;
    deptSpan[i] = j - i;
    i = j;
  }

  for (let i = 0; i < n; ) {
    let j = i + 1;
    while (
      j < n &&
      rows[j].period === rows[i].period &&
      rows[j].deptName === rows[i].deptName &&
      rows[j].parentCategoryName === rows[i].parentCategoryName
    ) {
      j += 1;
    }
    parentSpan[i] = j - i;
    i = j;
  }

  return { periodSpan, deptSpan, parentSpan };
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState<
    'monthly' | 'daily' | 'goods' | 'detail' | 'category' | 'plan' | 'monthPlan'
  >('monthly');
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSpecs, setSelectedSpecs] = useState<SpecFilterOption[]>([]);

  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const deptIdMapRef = useRef(deptIdMap);

  const [monthlySummary, setMonthlySummary] = useState<SummaryViewRow[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState('');

  const [dailySummary, setDailySummary] = useState<SummaryViewRow[]>([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState('');

  const [goodsSummary, setGoodsSummary] = useState<GoodsSummaryViewRow[]>([]);
  const [goodsLoading, setGoodsLoading] = useState(false);
  const [goodsError, setGoodsError] = useState('');

  const [detailOrders, setDetailOrders] = useState<ReportDetailOrder[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [categorySummary, setCategorySummary] = useState<CategoryViewRow[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');

  const [planSummary, setPlanSummary] = useState<PlanViewRow[]>([]);
  const [planFooter, setPlanFooter] = useState<PlanFooterTotals | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState('');

  const [monthPlanSummary, setMonthPlanSummary] = useState<MonthPlanViewRow[]>([]);
  const [monthPlanLoading, setMonthPlanLoading] = useState(false);
  const [monthPlanError, setMonthPlanError] = useState('');
  const [exporting, setExporting] = useState(false);

  const user = getUser();
  const showDeptFilter =
    user?.is_divide === true || user?.is_divide === 1 || user?.is_divide === '1';

  useEffect(() => {
    deptIdMapRef.current = deptIdMap;
  }, [deptIdMap]);

  useEffect(() => {
    if (!showDeptFilter) {
      setDeptOptions([]);
      setDeptIdMap({});
      setSelectedDepts([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await getAuthDepartments();
        const ok = res.error === false || Number(res.error) === 0;
        if (cancelled || !ok || !Array.isArray(res.data)) return;
        const nextMap: Record<string, string> = {};
        const nextOptions: string[] = [];
        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0) return;
          if (!nextMap[title]) {
            nextMap[title] = String(id);
            nextOptions.push(title);
          }
        });
        setDeptIdMap(nextMap);
        setDeptOptions(nextOptions);
      } catch {
        if (!cancelled) {
          setDeptOptions([]);
          setDeptIdMap({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDeptFilter]);

  /** 所有报表接口共用同一套筛选参数 */
  const buildReportFilterParams = useCallback((): ReportFilterParams => {
    const idMap = deptIdMapRef.current;
    const departmentIds = selectedDepts
      .map((title) => Number(idMap[title]) || idMap[title])
      .filter((id) => id !== '' && id != null && Number(id) > 0);
    const specIds = selectedSpecs
      .map((s) => Number(s.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    return {
      date_begin: dateFrom || undefined,
      date_end: dateTo || undefined,
      department_ids: departmentIds.length > 0 ? departmentIds : undefined,
      spec_ids: specIds.length > 0 ? specIds : undefined,
    };
  }, [selectedDepts, dateFrom, dateTo, selectedSpecs]);

  const fetchMonthSummary = useCallback(async () => {
    setMonthlyLoading(true);
    setMonthlyError('');
    try {
      const res = await getMonthSummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setMonthlySummary([]);
        setMonthlyError(res.message || res.msg || '月度汇总加载失败');
        return;
      }
      setMonthlySummary(mapSummaryRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setMonthlySummary([]);
      setMonthlyError('月度汇总加载失败，请稍后重试');
    } finally {
      setMonthlyLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchDailySummary = useCallback(async () => {
    setDailyLoading(true);
    setDailyError('');
    try {
      const res = await getDailySummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setDailySummary([]);
        setDailyError(res.message || res.msg || '日汇总加载失败');
        return;
      }
      setDailySummary(mapSummaryRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setDailySummary([]);
      setDailyError('日汇总加载失败，请稍后重试');
    } finally {
      setDailyLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchGoodsSummary = useCallback(async () => {
    setGoodsLoading(true);
    setGoodsError('');
    try {
      const res = await getGoodsSummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setGoodsSummary([]);
        setGoodsError(res.message || res.msg || '物资汇总加载失败');
        return;
      }
      setGoodsSummary(mapGoodsSummaryRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setGoodsSummary([]);
      setGoodsError('物资汇总加载失败，请稍后重试');
    } finally {
      setGoodsLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchDetailOrders = useCallback(async () => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await getReportDetails(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setDetailOrders([]);
        setDetailError(res.message || res.msg || '明细表加载失败');
        return;
      }
      setDetailOrders(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDetailOrders([]);
      setDetailError('明细表加载失败，请稍后重试');
    } finally {
      setDetailLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchCategorySummary = useCallback(async () => {
    setCategoryLoading(true);
    setCategoryError('');
    try {
      const res = await getCategorySummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setCategorySummary([]);
        setCategoryError(res.message || res.msg || '分类统计加载失败');
        return;
      }
      setCategorySummary(mapCategoryRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setCategorySummary([]);
      setCategoryError('分类统计加载失败，请稍后重试');
    } finally {
      setCategoryLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchPurchasePlanSummary = useCallback(async () => {
    setPlanLoading(true);
    setPlanError('');
    try {
      const params = buildReportFilterParams();
      const res = await getPurchasePlanSummary({
        date_begin: params.date_begin,
        date_end: params.date_end,
        department_ids: params.department_ids,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setPlanSummary([]);
        setPlanFooter(null);
        setPlanError(res.message || res.msg || '年度预算表加载失败');
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      setPlanSummary(mapPurchasePlanRows(list));
      setPlanFooter(list.length > 0 ? mapPurchasePlanFooter(list) : null);
    } catch {
      setPlanSummary([]);
      setPlanFooter(null);
      setPlanError('年度预算表加载失败，请稍后重试');
    } finally {
      setPlanLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchMonthPurchasePlanSummary = useCallback(async () => {
    setMonthPlanLoading(true);
    setMonthPlanError('');
    try {
      const params = buildReportFilterParams();
      const res = await getMonthPurchasePlanSummary({
        date_begin: params.date_begin,
        date_end: params.date_end,
        department_ids: params.department_ids,
        spec_ids: params.spec_ids,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setMonthPlanSummary([]);
        setMonthPlanError(res.message || res.msg || '月度计划表加载失败');
        return;
      }
      setMonthPlanSummary(mapMonthPurchasePlanRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setMonthPlanSummary([]);
      setMonthPlanError('月度计划表加载失败，请稍后重试');
    } finally {
      setMonthPlanLoading(false);
    }
  }, [buildReportFilterParams]);

  useEffect(() => {
    if (activeTab === 'monthly') void fetchMonthSummary();
    if (activeTab === 'daily') void fetchDailySummary();
    if (activeTab === 'goods') void fetchGoodsSummary();
    if (activeTab === 'detail') void fetchDetailOrders();
    if (activeTab === 'category') void fetchCategorySummary();
    if (activeTab === 'plan') void fetchPurchasePlanSummary();
    if (activeTab === 'monthPlan') void fetchMonthPurchasePlanSummary();
  }, [
    activeTab,
    fetchMonthSummary,
    fetchDailySummary,
    fetchGoodsSummary,
    fetchDetailOrders,
    fetchCategorySummary,
    fetchPurchasePlanSummary,
    fetchMonthPurchasePlanSummary,
  ]);

  const refreshActiveReport = () => {
    if (activeTab === 'monthly') void fetchMonthSummary();
    if (activeTab === 'daily') void fetchDailySummary();
    if (activeTab === 'goods') void fetchGoodsSummary();
    if (activeTab === 'detail') void fetchDetailOrders();
    if (activeTab === 'category') void fetchCategorySummary();
    if (activeTab === 'plan') void fetchPurchasePlanSummary();
    if (activeTab === 'monthPlan') void fetchMonthPurchasePlanSummary();
  };

  const isApiTabLoading =
    activeTab === 'monthly'
      ? monthlyLoading
      : activeTab === 'daily'
        ? dailyLoading
        : activeTab === 'goods'
          ? goodsLoading
          : activeTab === 'detail'
            ? detailLoading
            : activeTab === 'category'
              ? categoryLoading
              : activeTab === 'plan'
                ? planLoading
                : activeTab === 'monthPlan'
                  ? monthPlanLoading
                  : false;

  const detailOrderCount = detailOrders.length;
  const detailLineCount = detailOrders.reduce(
    (sum, order) => sum + (Array.isArray(order.lists) ? order.lists.length : 0),
    0,
  );

  const hasActiveFilters = selectedDepts.length > 0 || dateFrom || dateTo || selectedSpecs.length > 0;

  const dateQueryLabel = (() => {
    if (dateFrom && dateTo) return `${dateFrom} 至 ${dateTo}`;
    if (dateFrom) return `${dateFrom} 起`;
    if (dateTo) return `截至 ${dateTo}`;
    return '';
  })();

  const handleReset = () => {
    setSelectedDepts([]);
    setDateFrom('');
    setDateTo('');
    setSelectedSpecs([]);
  };

  const handleExportReport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await exportReport({
        ...buildReportFilterParams(),
        report_type: TAB_TO_EXPORT_TYPE[activeTab],
      });
      const ok = res.error === false || Number(res.error) === 0;
      const fileUrl = String(res.url || '').trim();
      if (!ok || !fileUrl) {
        await alertError(res.message || res.msg || '报表导出失败', '导出失败');
        return;
      }
      const filename = fileUrl.split('/').pop()?.split('?')[0] || 'report.xlsx';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      await alertError('报表导出失败，请稍后重试', '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const recordCountLabel =
    activeTab === 'monthly'
      ? monthlySummary.length
      : activeTab === 'daily'
        ? dailySummary.length
        : activeTab === 'goods'
          ? goodsSummary.length
          : activeTab === 'detail'
            ? `${detailOrderCount} 单 / ${detailLineCount} 行`
            : activeTab === 'plan'
              ? planSummary.length
              : activeTab === 'monthPlan'
                ? monthPlanSummary.length
                : categorySummary.length;

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 md:px-6 pb-4 md:pb-6">

        {/* Filter Card */}
        <div className="bg-white rounded-xl px-4 py-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Filter inputs – same row */}
            <div className="flex items-center gap-3 flex-wrap flex-1">
            {showDeptFilter && (
              <div className="min-w-[14rem] max-w-xs w-56">
                <SearchableSelect
                  multiple
                  options={deptOptions}
                  value={selectedDepts}
                  onChange={(v) => setSelectedDepts(Array.isArray(v) ? v : v ? [v] : [])}
                  placeholder="搜索 / 选择科室"
                  allLabel="全部科室"
                  icon="ri-hospital-line"
                  searchPlaceholder="继续搜索科室..."
                />
              </div>
            )}
            <div className="flex items-center gap-2">
<div className="w-56">
                <DateRangePicker
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={setDateFrom}
                  onDateToChange={setDateTo}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="min-w-[14rem] max-w-sm w-72">
                <SpecFilterSelect
                  value={selectedSpecs}
                  onChange={setSelectedSpecs}
                  placeholder="搜索 / 选择规格"
                  searchPlaceholder="继续搜索规格..."
                />
              </div>
            </div>
            </div>{/* end filter inputs */}
            <div className="flex items-center gap-2 shrink-0 self-end">
            {dateQueryLabel && (
              <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-primary-50 border border-primary-100 text-xs text-primary-700 max-w-full">
                <i className="ri-calendar-check-line shrink-0" />
                <span className="truncate">
                  当前查询时间：
                  <span className="font-medium">{dateQueryLabel}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                  }}
                  className="w-5 h-5 flex items-center justify-center rounded-full text-primary-500 hover:bg-primary-100 hover:text-primary-700 transition-colors shrink-0"
                  title="清除日期筛选"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </span>
            )}
            {(activeTab === 'monthly' ||
              activeTab === 'daily' ||
              activeTab === 'goods' ||
              activeTab === 'detail' ||
              activeTab === 'category' ||
              activeTab === 'plan' ||
              activeTab === 'monthPlan') && (
              <button
                type="button"
                onClick={refreshActiveReport}
                disabled={isApiTabLoading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap border border-background-300 disabled:opacity-50"
              >
                <i className={`ri-refresh-line ${isApiTabLoading ? 'animate-spin' : ''}`} />
                刷新
              </button>
            )}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap border border-background-300"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            )}
            </div>
          </div>{/* end flex row */}
        </div>

        {/* Table content Card */}
        <div className="bg-white rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-background-200 bg-background-50 flex-wrap gap-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300 hover:text-foreground-800'
                  }`}
                >
                  <i className={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-foreground-400">共 {recordCountLabel} 条记录</span>
              <button
                type="button"
                onClick={() => void handleExportReport()}
                disabled={exporting || isApiTabLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-600 bg-transparent border border-blue-300 hover:bg-blue-50 hover:border-blue-400 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`ri-download-2-line ${exporting ? 'animate-pulse' : ''}`} />
                {exporting ? '导出中...' : '下载报表'}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {/* ===== 月度汇总表 ===== */}
            {activeTab === 'monthly' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs w-14">序号</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">月份</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">科室</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">物资数量</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购金额</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">运费</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购总额</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyLoading ? (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载月度汇总...
                      </td>
                    </tr>
                  ) : monthlyError ? (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{monthlyError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchMonthSummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : monthlySummary.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    monthlySummary.map((row, idx) => (
                      <tr key={`${row.deptName}-${row.period}-${idx}`} className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-3 text-center text-foreground-500">{idx + 1}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.period}</td>
                        <td className="py-3 px-4 font-medium text-foreground-800">{row.deptName}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.goodsQuantity}</td>
                        <td className="py-3 px-4 text-right text-foreground-700">¥{row.amount.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-foreground-700">¥{row.cost.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600">¥{row.totalAmount.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!monthlyLoading && !monthlyError && monthlySummary.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={3} className="py-3 px-4 text-right text-xs font-medium text-foreground-700">合计</td>
                      <td className="py-3 px-4 text-center text-xs font-bold text-foreground-900">
                        {monthlySummary.reduce((s, r) => s + r.goodsQuantity, 0)}
                      </td>
                      <td className="py-3 px-4 text-right text-xs font-bold text-foreground-900">
                        ¥{monthlySummary.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right text-xs font-bold text-foreground-900">
                        ¥{monthlySummary.reduce((s, r) => s + r.cost, 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-primary-600">
                        ¥{monthlySummary.reduce((s, r) => s + r.totalAmount, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 日汇总表 ===== */}
            {activeTab === 'daily' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs w-14">序号</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">日期</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">科室</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">物资数量</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购金额</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">运费</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购总额</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyLoading ? (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载日汇总...
                      </td>
                    </tr>
                  ) : dailyError ? (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{dailyError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchDailySummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : dailySummary.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    dailySummary.map((row, idx) => (
                      <tr key={`${row.deptName}-${row.period}-${idx}`} className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-3 text-center text-foreground-500">{idx + 1}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.period}</td>
                        <td className="py-3 px-4 font-medium text-foreground-800">{row.deptName}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.goodsQuantity}</td>
                        <td className="py-3 px-4 text-right text-foreground-700">¥{row.amount.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-foreground-700">¥{row.cost.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600">¥{row.totalAmount.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!dailyLoading && !dailyError && dailySummary.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={3} className="py-3 px-4 text-right text-xs font-medium text-foreground-700">合计</td>
                      <td className="py-3 px-4 text-center text-xs font-bold text-foreground-900">
                        {dailySummary.reduce((s, r) => s + r.goodsQuantity, 0)}
                      </td>
                      <td className="py-3 px-4 text-right text-xs font-bold text-foreground-900">
                        ¥{dailySummary.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right text-xs font-bold text-foreground-900">
                        ¥{dailySummary.reduce((s, r) => s + r.cost, 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-primary-600">
                        ¥{dailySummary.reduce((s, r) => s + r.totalAmount, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 物资汇总表 ===== */}
            {activeTab === 'goods' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-14">序号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资类别</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">规格型号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">编码</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">采购数量</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">单位</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">单价</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">合计金额</th>
                  </tr>
                </thead>
                <tbody>
                  {goodsLoading ? (
                    <tr>
                      <td colSpan={9} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载物资汇总...
                      </td>
                    </tr>
                  ) : goodsError ? (
                    <tr>
                      <td colSpan={9} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{goodsError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchGoodsSummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : goodsSummary.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    goodsSummary.map((row, idx) => (
                      <tr
                        key={`${row.goodsName}-${row.skuName}-${row.code}-${idx}`}
                        className="hover:bg-background-50 transition-colors"
                      >
                        <td className="py-2.5 px-3 text-center text-foreground-500 text-xs">{idx + 1}</td>
                        <td className="py-2.5 px-3 text-foreground-700 text-xs max-w-[160px] truncate">
                          {row.categoryName}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[160px] truncate">
                          {row.goodsName}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[140px] truncate">
                          {row.skuName}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[120px] truncate">
                          {row.code}
                        </td>
                        <td
                          className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${
                            row.quantity < 0 ? 'text-orange-600 font-medium' : 'text-foreground-700'
                          }`}
                        >
                          {row.quantity}
                        </td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 whitespace-nowrap text-xs">
                          {row.unit}
                        </td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 whitespace-nowrap text-xs">
                          ¥{row.price.toFixed(2)}
                        </td>
                        <td
                          className={`py-2.5 px-3 text-right font-semibold whitespace-nowrap text-xs ${
                            row.amount < 0 ? 'text-orange-600' : 'text-primary-600'
                          }`}
                        >
                          ¥{row.amount.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!goodsLoading && !goodsError && goodsSummary.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={5} className="py-3 px-3 text-right text-xs font-medium text-foreground-700">
                        合计
                      </td>
                      <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                        {goodsSummary.reduce((s, r) => s + r.quantity, 0)}
                      </td>
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3" />
                      <td className="py-3 px-3 text-right text-sm font-bold text-primary-600">
                        ¥{goodsSummary.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 明细表 ===== */}
            {activeTab === 'detail' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-14">序号</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">完成日期</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">科室名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">订单号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资名</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">规格型号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">编码</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">单位</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">单价</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">数量</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">小计</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">物资金额</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">运费</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">订单金额</th>
                  </tr>
                </thead>
                <tbody>
                  {detailLoading ? (
                    <tr>
                      <td colSpan={14} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载明细...
                      </td>
                    </tr>
                  ) : detailError ? (
                    <tr>
                      <td colSpan={14} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{detailError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchDetailOrders()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : detailOrders.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    detailOrders.flatMap((order, orderIdx) => {
                      const items = Array.isArray(order.lists) && order.lists.length > 0
                        ? order.lists
                        : [null];
                      const rowSpan = items.length;
                      const goodsAmount = Number(order.amount ?? 0) || 0;
                      const freight = Number(order.cost ?? 0) || 0;
                      const orderTotal = Number(order.total ?? 0) || 0;
                      const orderDate = order.complete_time
                        ? String(order.complete_time).slice(0, 10)
                        : (order.created_at ? String(order.created_at).slice(0, 10) : '-');
                      const deptName = order.department_name || '-';

                      return items.map((item, itemIdx) => {
                        const qty = item ? Number(item.quantity ?? 0) || 0 : 0;
                        const price = item ? Number(item.price ?? 0) || 0 : 0;
                        const subtotal = item ? Number(item.amount ?? 0) || 0 : 0;
                        return (
                          <tr
                            key={`${order.id}-${item?.id ?? 'empty'}-${itemIdx}`}
                            className="hover:bg-background-50 transition-colors"
                          >
                            {itemIdx === 0 && (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="py-2.5 px-3 text-center text-foreground-500 text-xs align-middle"
                                >
                                  {orderIdx + 1}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="py-2.5 px-3 text-center text-foreground-700 whitespace-nowrap text-xs align-middle"
                                >
                                  {orderDate}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="py-2.5 px-3 text-foreground-700 whitespace-nowrap text-xs align-middle"
                                >
                                  {deptName}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="py-2.5 px-3 text-foreground-800 font-medium whitespace-nowrap text-xs align-middle"
                                >
                                  {order.order_sn || order.id}
                                </td>
                              </>
                            )}
                            <td className="py-2.5 px-3 text-foreground-800 max-w-[160px] truncate text-xs">
                              {item?.goods_name || '-'}
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600 max-w-[120px] truncate text-xs">
                              {item?.sku_name || '-'}
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600 max-w-[100px] truncate text-xs">
                              {item?.sku || '-'}
                            </td>
                            <td className="py-2.5 px-3 text-center text-foreground-600 whitespace-nowrap text-xs">
                              {item?.unit || '-'}
                            </td>
                            <td className="py-2.5 px-3 text-right text-foreground-700 whitespace-nowrap text-xs">
                              {item ? `¥${price.toFixed(2)}` : '-'}
                            </td>
                            <td
                              className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${
                                item && qty < 0 ? 'text-orange-600 font-medium' : 'text-foreground-700'
                              }`}
                            >
                              {item ? qty : '-'}
                            </td>
                            <td
                              className={`py-2.5 px-3 text-right font-semibold whitespace-nowrap text-xs ${
                                item && subtotal < 0 ? 'text-orange-600' : 'text-primary-600'
                              }`}
                            >
                              {item ? `¥${subtotal.toFixed(2)}` : '-'}
                            </td>
                            {itemIdx === 0 && (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className={`py-2.5 px-3 text-right whitespace-nowrap text-xs align-middle ${
                                    goodsAmount < 0 ? 'text-orange-600 font-medium' : 'text-foreground-700'
                                  }`}
                                >
                                  ¥{goodsAmount.toFixed(2)}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="py-2.5 px-3 text-right text-foreground-700 whitespace-nowrap text-xs align-middle"
                                >
                                  ¥{freight.toFixed(2)}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className={`py-2.5 px-3 text-right font-semibold whitespace-nowrap text-xs align-middle ${
                                    orderTotal < 0 ? 'text-orange-600' : 'text-primary-600'
                                  }`}
                                >
                                  ¥{orderTotal.toFixed(2)}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      });
                    })
                  )}
                </tbody>
                {!detailLoading && !detailError && detailOrders.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={11} className="py-3 px-3 text-right text-xs font-medium text-foreground-700">
                        合计
                      </td>
                      <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                        ¥{detailOrders.reduce((s, o) => s + (Number(o.amount ?? 0) || 0), 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                        ¥{detailOrders.reduce((s, o) => s + (Number(o.cost ?? 0) || 0), 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-3 text-right text-sm font-bold text-primary-600">
                        ¥{detailOrders.reduce((s, o) => s + (Number(o.total ?? 0) || 0), 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 年度预算表 ===== */}
            {activeTab === 'plan' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs w-14">序号</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">月份</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">科室</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">本月使用</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">使用比例</th>
                  </tr>
                </thead>
                <tbody>
                  {planLoading ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载年度预算表...
                      </td>
                    </tr>
                  ) : planError ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{planError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchPurchasePlanSummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : planSummary.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-foreground-400 text-sm">
                        暂无开启年度预算的科室
                      </td>
                    </tr>
                  ) : (
                    planSummary.map((row, idx) => (
                      <tr
                        key={`${row.period}-${row.deptName}-${idx}`}
                        className="hover:bg-background-50 transition-colors"
                      >
                        <td className="py-3 px-3 text-center text-foreground-500">{idx + 1}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.period}</td>
                        <td className="py-3 px-4 font-medium text-foreground-800">{row.deptName}</td>
                        <td className="py-3 px-4 text-right text-foreground-700">¥{row.monthUsed.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center">
                            <UsageRatioBar ratio={row.usageRatio} />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!planLoading && !planError && planSummary.length > 0 && planFooter && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={5} className="py-3 px-4">
                        <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 text-xs">
                          <span className="text-foreground-600">
                            年度总预算：
                            <span className="ml-1 font-bold text-foreground-900">
                              ¥{planFooter.quota.toFixed(2)}
                            </span>
                          </span>
                          <span className="text-foreground-600">
                            今年剩余：
                            <span className="ml-1 font-bold text-foreground-900">
                              ¥{planFooter.remainAmount.toFixed(2)}
                            </span>
                          </span>
                          <span className="text-foreground-600">
                            已使用额度：
                            <span className="ml-1 font-bold text-foreground-900">
                              ¥{planFooter.usedAmount.toFixed(2)}
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-foreground-600">
                            已使用占比：
                            <UsageRatioBar ratio={planFooter.usageRatio} barClassName="w-28" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 月度计划表 ===== */}
            {activeTab === 'monthPlan' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-2 text-center text-foreground-600 font-semibold text-xs w-12 whitespace-nowrap">序号</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">月份</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">科室名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">规格名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">编码</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">单位</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">计划数</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">已使用</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">已退货</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">剩余可用</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">单价</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">采购总价</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">已使用总价</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">退货金额</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">剩余金额</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">已使用比例</th>
                  </tr>
                </thead>
                <tbody>
                  {monthPlanLoading ? (
                    <tr>
                      <td colSpan={17} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载月度计划表...
                      </td>
                    </tr>
                  ) : monthPlanError ? (
                    <tr>
                      <td colSpan={17} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{monthPlanError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchMonthPurchasePlanSummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : monthPlanSummary.length === 0 ? (
                    <tr>
                      <td colSpan={17} className="py-8 text-center text-foreground-400 text-sm">
                        暂无月度采购计划数据
                      </td>
                    </tr>
                  ) : (
                    monthPlanSummary.map((row, idx) => (
                      <tr
                        key={`${row.period}-${row.deptName}-${row.code}-${idx}`}
                        className="hover:bg-background-50 transition-colors"
                      >
                        <td className="py-2.5 px-2 text-center text-foreground-500 text-xs">{idx + 1}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">{row.period}</td>
                        <td className="py-2.5 px-3 text-foreground-800 text-xs whitespace-nowrap">{row.deptName}</td>
                        <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[140px] truncate">{row.goodsName}</td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[120px] truncate">{row.specName}</td>
                        <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap">{row.code}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">{row.unit}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-700 text-xs font-medium whitespace-nowrap">{row.planQuantity}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-700 text-xs whitespace-nowrap">{row.usedQuantity}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">{row.returnQuantity}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-800 text-xs font-medium whitespace-nowrap">{row.remainQuantity}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">¥{row.price.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">¥{row.planAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">¥{row.usedAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-600 text-xs whitespace-nowrap">¥{row.returnAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-primary-600 text-xs font-semibold whitespace-nowrap">¥{row.remainAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-left">
                          <UsageRatioBar ratio={row.usageRatio} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!monthPlanLoading && !monthPlanError && monthPlanSummary.length > 0 && (
                  <tfoot>
                    {(() => {
                      const totalPlanAmount = monthPlanSummary.reduce((s, r) => s + r.planAmount, 0);
                      const totalUsedAmount = monthPlanSummary.reduce((s, r) => s + r.usedAmount, 0);
                      const totalReturnAmount = monthPlanSummary.reduce((s, r) => s + r.returnAmount, 0);
                      const totalRemainAmount = monthPlanSummary.reduce((s, r) => s + r.remainAmount, 0);
                      const usedRatio =
                        totalPlanAmount > 0
                          ? ((totalUsedAmount - totalReturnAmount) / totalPlanAmount) * 100
                          : 0;
                      return (
                        <tr className="bg-background-50">
                          <td colSpan={7} className="py-3 px-3 text-right text-xs font-medium text-foreground-700">
                            合计
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.planQuantity, 0)}
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.usedQuantity, 0)}
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.returnQuantity, 0)}
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.remainQuantity, 0)}
                          </td>
                          <td />
                          <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                            ¥{totalPlanAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                            ¥{totalUsedAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                            ¥{totalReturnAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-right text-sm font-bold text-primary-600">
                            ¥{totalRemainAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-left">
                            <UsageRatioBar ratio={usedRatio} />
                          </td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 分类统计表 ===== */}
            {activeTab === 'category' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs w-14">序号</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">月份</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">科室</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">一级分类</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">二级分类</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购金额</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">运费</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购总额</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryLoading ? (
                    <tr>
                      <td colSpan={8} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载分类统计...
                      </td>
                    </tr>
                  ) : categoryError ? (
                    <tr>
                      <td colSpan={8} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{categoryError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchCategorySummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : categorySummary.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    (() => {
                      const { periodSpan, deptSpan, parentSpan } =
                        buildCategoryMergeSpans(categorySummary);
                      return categorySummary.map((row, idx) => (
                        <tr
                          key={`${row.period}-${row.deptName}-${row.parentCategoryName}-${row.categoryName}-${idx}`}
                          className="hover:bg-background-50 transition-colors"
                        >
                          <td className="py-3 px-3 text-center text-foreground-500 align-middle">
                            {idx + 1}
                          </td>
                          {periodSpan[idx] > 0 && (
                            <td
                              rowSpan={periodSpan[idx]}
                              className="py-3 px-4 text-center text-foreground-600 align-middle"
                            >
                              {row.period}
                            </td>
                          )}
                          {deptSpan[idx] > 0 && (
                            <td
                              rowSpan={deptSpan[idx]}
                              className="py-3 px-4 font-medium text-foreground-800 align-middle"
                            >
                              {row.deptName}
                            </td>
                          )}
                          {parentSpan[idx] > 0 && (
                            <td
                              rowSpan={parentSpan[idx]}
                              className="py-3 px-4 text-foreground-700 align-middle"
                            >
                              {row.parentCategoryName}
                            </td>
                          )}
                          <td className="py-3 px-4 text-foreground-600">{row.categoryName}</td>
                          <td className="py-3 px-4 text-right text-foreground-700">
                            ¥{row.amount.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right text-foreground-700">
                            ¥{row.cost.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-primary-600">
                            ¥{row.totalAmount.toFixed(2)}
                          </td>
                        </tr>
                      ));
                    })()
                  )}
                </tbody>
                {!categoryLoading && !categoryError && categorySummary.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={5} className="py-3 px-4 text-right text-xs font-medium text-foreground-700">
                        合计
                      </td>
                      <td className="py-3 px-4 text-right text-xs font-bold text-foreground-900">
                        ¥{categorySummary.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right text-xs font-bold text-foreground-900">
                        ¥{categorySummary.reduce((s, r) => s + r.cost, 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-primary-600">
                        ¥{categorySummary.reduce((s, r) => s + r.totalAmount, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}